// NOTICE: KAPTION-INTEGRATION
// smart-availability.UI — how a person or a team reads in a picker once the account's
// endpoint has said who can take work.
//
// Pure functions over (row, info). `info` is what the store holds for that id, or undefined
// when the endpoint said nothing about it; undefined always yields exactly today's
// rendering (smart-availability.UI.2 — sin dato no se tacha a nadie). The words are for
// everyone; the numbers only when `showCounts` says so (smart-availability.UI.6).

const I18N = 'CONVERSATION.SMART_AVAILABILITY';

/**
 * The presence-dot status the pickers already know how to draw.
 * available → the green dot ('online'); at capacity → the amber dot ('busy');
 * anything else → no dot ('offline', which the pickers hide).
 *
 * @param {Object} agent - a row from useAgentsList
 * @param {Object|undefined} info - the store's row for this agent
 * @returns {string|undefined}
 */
export const availabilityStatusFor = (agent, info) => {
  if (!info) return agent?.availability_status;
  if (info.available) return 'online';
  if (info.reason === 'over_cap') return 'busy';
  return 'offline';
};

/**
 * "Disponible" / "Al tope" / "Desconectado" / "Ocupado" / "Supervisa" / "Fuera de circulación".
 */
export const reasonLabel = (reason, t) => {
  const keys = {
    available: 'AVAILABLE',
    over_cap: 'OVER_CAP',
    offline: 'OFFLINE',
    busy: 'BUSY',
    supervisor: 'SUPERVISOR',
    no_group: 'NO_GROUP',
    excluded: 'EXCLUDED',
  };
  const key = keys[reason];
  return key ? t(`${I18N}.REASON.${key}`) : '';
};

/**
 * "3 / 5" when the endpoint gave a queue size and the viewer may see it.
 */
export const queueLabel = (info, showCounts) => {
  if (!showCounts || !info || !Number.isInteger(info.open)) return '';
  return Number.isInteger(info.limit)
    ? `${info.open} / ${info.limit}`
    : `${info.open}`;
};

/**
 * "Giuliano Annetti · Al tope · 11 / 5" — the row title in every picker.
 *
 * @param {Object} agent
 * @param {Object|undefined} info
 * @param {Function} t - vue-i18n translate
 * @param {boolean} showCounts
 * @returns {string} the agent's plain name when there is no info
 */
export const agentTitleWithQueue = (agent, info, t, showCounts = false) => {
  const name = agent?.name ?? '';
  if (!info) return name;
  const parts = [
    name,
    reasonLabel(info.reason, t),
    queueLabel(info, showCounts),
  ];
  return parts.filter(Boolean).join(' · ');
};

/**
 * "Cuentas en Desarrollo · 2 disponibles de 5" / "Grandes Cuentas · al tope" /
 * "Ventas Córdoba · nadie conectado"; the plain team name when there is no info.
 */
export const teamTitleWithQueue = (team, info, t, showCounts = false) => {
  const name = team?.name ?? '';
  if (!info) return name;
  let label;
  if (info.available) {
    label = t(`${I18N}.TEAM.AVAILABLE`, {
      available: info.available_members,
      connected: info.connected_members,
    });
  } else if (info.reason === 'over_cap') {
    label = t(`${I18N}.TEAM.OVER_CAP`);
  } else {
    label = t(`${I18N}.TEAM.NOBODY_CONNECTED`);
  }
  const parts = [name, label];
  if (showCounts && Number.isInteger(info.open_total)) {
    parts.push(t(`${I18N}.TEAM.OPEN_TOTAL`, { count: info.open_total }));
  }
  return parts.join(' · ');
};

/**
 * The team's presence-dot status: green when someone can take work, amber when everyone
 * connected is at capacity, none otherwise.
 */
export const teamStatusFor = (team, info, fallback) => {
  if (!info) return fallback;
  if (info.available) return 'online';
  if (info.reason === 'over_cap') return 'busy';
  return 'offline';
};

const rank = info => {
  if (!info) return 3; // unknown: after the people we know about, before nobody
  if (info.available) return 0;
  if (info.reason === 'over_cap') return 1;
  if (info.reason === 'busy') return 2;
  return 4;
};

/**
 * smart-availability.UI.3 — available first (fewest open first), then at capacity, then
 * busy, then unknown, then offline and the rest. Stable within a rank.
 *
 * @param {Array} agents
 * @param {Function} infoFor - agentId → info | undefined
 */
export const sortByAvailability = (agents, infoFor) =>
  agents
    .map((agent, index) => ({ agent, index, info: infoFor(agent.id) }))
    .sort((a, b) => {
      const byRank = rank(a.info) - rank(b.info);
      if (byRank !== 0) return byRank;
      const openA = Number.isInteger(a.info?.open) ? a.info.open : Infinity;
      const openB = Number.isInteger(b.info?.open) ? b.info.open : Infinity;
      if (openA !== openB) return openA - openB;
      return a.index - b.index;
    })
    .map(({ agent }) => agent);

/**
 * smart-availability.UI.4 — decorate a useAgentsList row for the pickers. The "None"
 * pseudo-agent (id 0) and rows the endpoint never mentioned come back untouched.
 */
export const decorateAgent = (agent, info, t, showCounts = false) => {
  if (!info || !agent?.id) return agent;
  return {
    ...agent,
    name: agentTitleWithQueue(agent, info, t, showCounts),
    availability_status: availabilityStatusFor(agent, info),
    kaption_smart_availability: info,
    assignmentLabel: agent.name,
    assignmentDetail: [
      reasonLabel(info.reason, t),
      queueLabel(info, showCounts),
    ]
      .filter(Boolean)
      .join(' · '),
  };
};

/**
 * "No se asignó: Giuliano está al tope, 11 abiertas de 5" — the line the person who picked
 * a row gets back when the resolver refused the pick.
 */
export const preferredRefusalMessage = (result, t) => {
  const lines = Array.isArray(result?.note_lines) ? result.note_lines : [];
  const trace = lines.find(line => typeof line === 'string' && line.trim());
  const head = t(`${I18N}.PICK_REFUSED`);
  return trace ? `${head}: ${trace}` : head;
};
